import { useEffect, useMemo } from 'react'
import { useFormContext, Controller } from 'react-hook-form'
import apiService from 'services/api'
import Select, { SelectOption } from 'components/FormUtils/Select'
import { DatePicker } from 'components/FormUtils/DatePicker'

const FULFILLMENT_OPTIONS: SelectOption[] = [
  { label: 'Ship to Address', value: 'ship_to_address' },
  { label: 'Pick Up Locally', value: 'pick_up_locally' },
  {
    label: 'Ship with Existing Request',
    value: 'ship_with_existing_order',
  },
]

const ErrorText = ({
  children,
  className = '',
}: {
  children: React.ReactNode
  className?: string
}) => <div className={`text-red-600 text-xs mt-1 ${className}`}>{children}</div>

export function MobileRequestDetailsStep() {
  const api = apiService()
  const {
    control,
    register, // <-- bring register back
    setValue,
    formState: { errors },
    watch,
  } = useFormContext()

  const distributorId = watch('distributor_id')

  const { data: distributors, isLoading: loadingDistributors } =
    api.useGetDistributors?.() ?? { data: undefined, isLoading: false }

  useEffect(() => {
    if (!distributorId) setValue('distributor_id', null)
  }, [distributorId, setValue])

  const distributorOptions = useMemo(() => {
    if (!distributors) return []
    return distributors.map((dist) => ({
      label: dist.name,
      value: dist.id,
    }))
  }, [distributors])

  return (
    <div className="p-2 space-y-4">
      {/* Date Needed */}
      <div>
        <Controller
          name="date_needed"
          control={control}
          render={({ field }) => (
            <>
              <DatePicker
                value={field.value}
                onChange={field.onChange}
                label="Date Needed *"
                className="mb-2"
              />
              {errors.date_needed && (
                <ErrorText>{errors.date_needed.message as string}</ErrorText>
              )}
            </>
          )}
        />
      </div>

      {/* Fulfillment Option */}
      <div>
        <Select
          control={control}
          name="fulfillment_option"
          label="Fulfillment Option"
          options={FULFILLMENT_OPTIONS}
          placeholder="Select fulfillment option"
          isMulti={false}
        />
        {errors.fulfillment_option && (
          <ErrorText>{errors.fulfillment_option.message as string}</ErrorText>
        )}
      </div>

      {/* Distributor */}
      <div>
        <Select
          control={control}
          name="distributor_id"
          label="Distributor"
          optional
          options={distributorOptions}
          placeholder={
            loadingDistributors
              ? 'Loading distributors...'
              : 'Select distributor'
          }
          isMulti={false}
          isClearable
          disabled={loadingDistributors}
        />
        {errors.distributor_id && (
          <ErrorText>{errors.distributor_id.message as string}</ErrorText>
        )}
      </div>

      {/* Special Instructions */}
      <div>
        <label className="text-base font-medium text-gray-800 select-none mb-1">
          Special Instructions
          <span className="text-sm italic font-normal"> - optional</span>
        </label>
        <textarea
          rows={3}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-fb-green-600"
          placeholder="Any special instructions or notes for this request..."
          {...register('special_instructions')}
        />
        {errors.special_instructions && (
          <ErrorText>{errors.special_instructions.message as string}</ErrorText>
        )}
      </div>
    </div>
  )
}
